import React from 'react';
import { Heart, Sparkles, ArrowRight } from 'lucide-react';

const Donate = () => {
  const impactCards = [
    { title: 'Meditation Programs', copy: 'Free weekly meditation classes and guidance for the community.' },
    { title: 'Dharma Teachings', copy: 'Regular teachings on Buddhist philosophy and lived practice.' },
    { title: "Children's Education", copy: 'Sunday school program for children with Buddhist values.' },
    { title: 'Temple Maintenance', copy: 'Upkeep of the temple facilities and meditation spaces.' },
    { title: 'Community Service', copy: 'Outreach programs and support for those in need.' },
    { title: 'Special Events', copy: 'Vesak celebrations, Poya day observances, cultural programs.' },
  ];

  return (
    <div className="min-h-screen bg-[#ede1c5] text-stone-900">
      {/* HERO */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/6689733/pexels-photo-6689733.jpeg)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#ede1c5]/85 via-[#ede1c5]/55 to-[#ede1c5]" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-amber-400/12 blur-[140px]" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 text-amber-700/90 tracking-[0.3em] text-[11px] uppercase mb-6">
            <span className="h-px w-12 bg-amber-600/60" />
            <Sparkles size={14} />
            <span>Dāna · Offering · Gratitude</span>
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] text-stone-900 max-w-3xl">
            Offer Dāna with<br />
            <span className="italic text-amber-800">a pure heart.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-stone-700 leading-relaxed">
            Your generosity sustains the temple, the monks, and the silent unfolding of the
            Dhamma.
          </p>
        </div>
      </section>

      {/* QUOTE / WHY */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-serif-display italic text-2xl md:text-3xl text-stone-900 leading-[1.4] mb-4">
            "Generosity brings happiness at every stage of its expression — in the intention to give,
            in the act of giving, and in the remembrance of having given."
          </p>
          <div className="flex items-center justify-center gap-3 mt-4 text-amber-800 text-sm tracking-[0.3em] uppercase">
            <span className="h-px w-10 bg-amber-600/60" /> The Buddha <span className="h-px w-10 bg-amber-600/60" />
          </div>

          <div className="mt-16 p-10 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-[#fbf5e3]/95 to-[#f3e7c4]/90 text-left">
            <div className="flex items-center gap-3 text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-4">
              <Heart size={14} /> Your support
            </div>
            <h3 className="font-serif-display text-3xl text-stone-900 mb-5">
              What your generosity sustains
            </h3>
            <div className="space-y-4 text-stone-700 leading-relaxed">
              <p>
                It supports our <span className="text-amber-800">monthly mortgage, utilities, insurance</span>, and the <span className="text-amber-800">monks' daily Dāna</span>.
              </p>
              <p>
                It maintains the temple, <span className="text-amber-800">meditation programs</span>, <span className="text-amber-800">Dhamma teachings</span>, and <span className="text-amber-800">Sunday school for children</span>.
              </p>
              <p className="text-stone-900">
                Through your kindness, the Buddha's teachings remain freely available to all who seek them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* QR CODES */}
      <section className="py-24 bg-[#e3d4af] border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              Scan to Donate
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">
              Choose your method
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Zelle */}
            <div className="group rounded-2xl border border-amber-500/40 bg-gradient-to-br from-[#fbf5e3]/95 to-[#f3e7c4]/90 hover:border-amber-500/40 transition-all p-10">
              <div className="text-center mb-8">
                <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-2">Bank Transfer</div>
                <h3 className="font-serif-display text-3xl text-stone-900">Zelle</h3>
                <p className="text-stone-600 mt-2 text-sm">Quick and easy bank-to-bank transfer</p>
              </div>
              <div className="flex justify-center mb-6">
                <div className="bg-white p-5 rounded-2xl shadow-2xl ring-1 ring-amber-300/30">
                  <img
                    src="https://customer-assets.emergentagent.com/job_alabama-viharaya/artifacts/djhenpc6_photo_2026-03-22_10-57-44.jpg"
                    alt="Zelle Donation QR Code"
                    className="w-60 h-60 object-contain"
                  />
                </div>
              </div>
              <div className="text-center text-xs tracking-[0.25em] uppercase text-stone-500">
                Scan with your Zelle app
              </div>
            </div>

            {/* PayPal */}
            <div className="group rounded-2xl border border-amber-500/40 bg-gradient-to-br from-[#fbf5e3]/95 to-[#f3e7c4]/90 hover:border-amber-500/40 transition-all p-10">
              <div className="text-center mb-8">
                <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-2">Card · Bank · PayPal</div>
                <h3 className="font-serif-display text-3xl text-stone-900">PayPal</h3>
                <p className="text-stone-600 mt-2 text-sm">Secure payment with credit / debit cards</p>
              </div>
              <div className="flex justify-center mb-6">
                <div className="bg-white p-5 rounded-2xl shadow-2xl ring-1 ring-amber-300/30">
                  <img
                    src="https://customer-assets.emergentagent.com/job_alabama-viharaya/artifacts/2pl8em7s_photo_2026-03-25_11-40-26.jpg"
                    alt="PayPal Donation QR Code"
                    className="w-60 h-60 object-contain"
                  />
                </div>
              </div>
              <div className="text-center text-xs tracking-[0.25em] uppercase text-stone-500">
                Scan with your PayPal app
              </div>
            </div>
          </div>

          <p className="text-center text-stone-500 mt-12 text-sm">
            Your generous donation supports meditation programs, teachings, and community service.
          </p>
        </div>
      </section>

      {/* IMPACT */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              Where it goes
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">
              Your offering supports
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {impactCards.map((c) => (
              <div
                key={c.title}
                className="p-7 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/80 hover:bg-[#fbf5e3]/95 hover:border-amber-500/40 transition-all"
              >
                <h3 className="font-serif-display text-xl text-stone-900 mb-3">{c.title}</h3>
                <p className="text-stone-600 leading-relaxed text-sm">{c.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THANK YOU */}
      <section className="py-24 bg-[#e3d4af] border-t border-amber-500/40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-serif-display text-3xl md:text-4xl text-stone-900 mb-4">
            🙏 Thank you for your <span className="italic text-amber-800">generosity</span>.
          </h3>
          <p className="text-stone-600 leading-relaxed mb-8">
            Every donation, regardless of size, helps preserve and share the Buddha's teachings.
            For questions about donations or sponsorship opportunities, please{' '}
            <a href="/contact" className="text-amber-800 hover:text-amber-100 underline-offset-4 hover:underline">
              contact us
            </a>
            .
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 border border-stone-300 hover:border-amber-300 text-stone-900 hover:text-amber-800 px-7 py-3 rounded-full text-sm tracking-wide"
          >
            Contact Us <ArrowRight size={15} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Donate;
